import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableCell, TableRow } from '@occc-app/design-system';
import { FraudCheckStatus, TransactionFragment } from '@occc/graphql';
import { sentenceCase } from '@occc-app/lib/strings';
import { readableDateWithTimezone } from '@occc-app/lib/format';
import { displayAmount } from '@occc-app/lib/currency';

import { routeCreator, shortenId, DATE_FORMAT } from '../utils';
import { labelByFraudCheckStatus } from '../constants';
import { FraudulentIconButton } from '../components/FraudulentIconButton';
import { getTransactionAddressWithLocationId } from '../TransactionView/utils';

type TransactionListItemProps = {
  transaction: TransactionFragment;
  shouldShowFraudStatus: boolean;
  onFraudDetailsClick?: (transaction: TransactionFragment) => void;
};

const TransactionsListItem = ({
  transaction,
  shouldShowFraudStatus,
  onFraudDetailsClick,
}: TransactionListItemProps) => {
  const navigate = useNavigate();

  const openFraudTransactionDetailsModal = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();

      if (onFraudDetailsClick) {
        onFraudDetailsClick(transaction);
      }
    },
    [transaction, onFraudDetailsClick],
  );

  const addressWithLocationId = getTransactionAddressWithLocationId(transaction);

  const onTableRowClick = () => navigate({ pathname: routeCreator.details(transaction.uuid) });

  const completedAt = readableDateWithTimezone(transaction.completedAt, DATE_FORMAT);

  return (
    <TableRow key={transaction.uuid} onClick={onTableRowClick}>
      <TableCell>{shortenId(transaction.uuid)}</TableCell>
      <TableCell>{addressWithLocationId}</TableCell>
      <TableCell>{transaction.authCode}</TableCell>
      <TableCell align='right'>{displayAmount(transaction.amount?.price || 0, false)}</TableCell>
      <TableCell>{sentenceCase(transaction.primaryStatus)}</TableCell>
      {shouldShowFraudStatus && (
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {labelByFraudCheckStatus[transaction.fraudCheckStatus]}
          {transaction.fraudCheckStatus === FraudCheckStatus.Fraudulent && (
            <FraudulentIconButton
              sx={{
                ml: 0.5,
              }}
              onClick={openFraudTransactionDetailsModal}
            />
          )}
        </TableCell>
      )}
      <TableCell>{completedAt}</TableCell>
      <TableCell>{transaction.customer?.phoneNumber}</TableCell>
    </TableRow>
  );
};

export default TransactionsListItem;
